import React, { useEffect, useState, useCallback, useRef } from "react";
import { Tabs, Menu } from "antd";
import { CloseOutlined, LogoutOutlined } from "@ant-design/icons";
import useClickAway from "ahooks/es/useClickAway";
import TabMapManager from "./TabMapManager";
import useMessage, { postMessage } from "./useMessage";
import "./index.css";

export function useTabsManage(maxLength) {
  const [tab] = React.useState(() => new TabMapManager(maxLength));
  const [_, forceUpdate] = React.useState({});
  return {
    tab,
    forceUpdate() {
      forceUpdate({});
    },
  };
}

export default function KeepAliveTab(props) {
  const {
    titleKeyMap = new Map(),
    maxLength = 10,
    outlet,
    location,
    onChange,
    tabsManage,
  } = props;
  const { tab, forceUpdate } = tabsManage || useTabsManage(maxLength);
  const { pathname, search } = location;

  useEffect(() => {
    // "/" is a layout route with <Navigate replace>, skip to avoid redirect loop
    if (pathname === '/') return;
    if (!tab.has(pathname)) {
      const title = titleKeyMap.get(pathname) || pathname;
      tab.set(pathname, {
        location,
        pathname,
        outlet,
        search,
        title,
      });
    } else {
      tab.get(pathname).search = search;
    }
    forceUpdate();
  }, [location]);

  const tabs = tab.getTabs();
  const closeable = tabs.length > 1;

  const onCloseTab = useCallback(
    (key) => {
      if (key === pathname) {
        const newCurrent = tab.getPrev(key) || tab.getNext(key);
        tab.delete(key);
        onChange && onChange(newCurrent.pathname + (newCurrent.search || ''));
      } else {
        tab.delete(key);
        forceUpdate();
      }
    },
    [pathname, tab]
  );

  const tabItems = tabs.map(([_, current]) => ({
    key: current.pathname,
    label: (
      <React.Fragment>
        <div
          className="cn-keepalive-layout-tab-title"
          onContextMenu={(e) => {
            e.stopPropagation();
            e.preventDefault();
            postMessage("menu-context-menu", {
              x: e.clientX,
              y: e.clientY,
              key: current.pathname,
            });
          }}
        >
          <span className="text" title={current.title}>
            {current.title}
          </span>
        </div>
        {closeable ? (
          <CloseOutlined
            className="tab-close-icon"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCloseTab(current.pathname);
            }}
          />
        ) : null}
      </React.Fragment>
    ),
    children: <div>{current.outlet}</div>,
  }));

  return (
    <React.Fragment>
      <Tabs
        size="small"
        animated={false}
        className="cn-keepalive-layout-tab"
        activeKey={pathname}
        onChange={(key) => {
          const current = tab.get(key);
          if (current && onChange) {
            onChange(current.pathname + (current.search || ''));
          }
        }}
        items={tabItems}
      />
      <ContextMenu
        tab={tab}
        onContextMenuClick={({ key, action }) => {
          if (action === "close") {
            onCloseTab(key);
          }
          if (action === "close-other") {
            tab.deleteOther(key);
            const current = tab.get(key);
            onChange && onChange(current.pathname + (current.search || ''));
          }
        }}
      />
    </React.Fragment>
  );
}

function ContextMenu(props) {
  const { onContextMenuClick, tab } = props;
  const contextMenuWrapRef = useRef();
  const [position, setPosition] = useState({ display: false });
  const size = tab.size;

  useMessage(
    "menu-context-menu",
    (data) => {
      setPosition({ display: true, ...data });
    },
    [setPosition]
  );

  useClickAway(() => {
    setPosition({ display: false });
  }, contextMenuWrapRef);

  if (!position.display) {
    return null;
  }

  const menuItems = [];
  if (size > 1) {
    menuItems.push({
      key: "close",
      icon: <CloseOutlined />,
      label: "Close Tab",
      onClick: () => {
        onContextMenuClick({ key: position.key, action: "close" });
        setPosition({ display: false });
      },
    });
    menuItems.push({
      key: "close-other",
      icon: <LogoutOutlined />,
      label: "Close Other Tabs",
      onClick: () => {
        onContextMenuClick({ key: position.key, action: "close-other" });
        setPosition({ display: false });
      },
    });
  }

  return (
    <div
      className="keep-alive-context-menu"
      ref={contextMenuWrapRef}
      style={{ left: position.x, top: position.y }}
    >
      <Menu mode="vertical" selectable={false} items={menuItems} />
    </div>
  );
}
